// @flow
import axios from 'axios';
import inject from '@rover/utilities/injector';
import { camelize, snakeify } from '@rover/utilities/object-utilities';
import type { CreditCardSummary, CreditCardAddSummary } from '@rover/react-lib/types';
import { t } from '@lingui/macro';

import { BILLING_ADDRESS_FIELDS } from '@rover/react-lib/constants/creditCard.constants';

import DefaultAPIErrorHandler from './DefaultAPIErrorHandler';

export const formatBilling = (data: any) => {
  const snakeData = snakeify(data, true);
  const fields = {
    billing_address: {},
    postal_code: snakeData.zip,
    country_code: snakeData.country_code,
  };
  Object.keys(snakeData).forEach(key => {
    if (!BILLING_ADDRESS_FIELDS.includes(key)) {
      fields[key] = snakeData[key];
    }
  });
  BILLING_ADDRESS_FIELDS.forEach(f => {
    fields.billing_address[f] = snakeData[f] || '';
  });
  return fields;
};

export const getCards = async (): Promise<Array<CreditCardSummary>> => {
  try {
    const paymentMethods = inject('Rover.urls.paymentMethods');
    const response = await axios.get(paymentMethods);
    return camelize(response.data, true).filter(i => i.type === 'creditcard');
  } catch (error) {
    throw DefaultAPIErrorHandler(error, t`There was an error fetching credit cards.`);
  }
};

export const getCard = async (cardUrl: string): Promise<CreditCardSummary> => {
  try {
    const response = await axios.get(cardUrl);
    return camelize(response.data, true);
  } catch (error) {
    throw DefaultAPIErrorHandler(error, 'There was an error fetching a credit card.');
  }
};

export const setDefaultCard = async (cardUrl: string): Promise<CreditCardSummary> => {
  try {
    const defaultPaymentUrl = inject('Rover.urls.defaultPaymentUrl');
    const response = await axios.put(defaultPaymentUrl, { url: cardUrl });
    return camelize(response.data, true);
  } catch (error) {
    throw DefaultAPIErrorHandler(error, t`There was an error setting the default credit card.`);
  }
};

export const removeCard = async (cardUrl: string): Promise<boolean> => {
  try {
    await axios.delete(cardUrl);
  } catch (error) {
    throw DefaultAPIErrorHandler(error, t`There was an error removing a credit card.`);
  }
  return true;
};

export const saveCard = async (body: CreditCardAddSummary): Promise<string> => {
  if (!(body.formVersion === 'braintree' || body.formVersion === 'stripe')) {
    throw DefaultAPIErrorHandler(
      {
        response: {
          status: 400,
          response: {
            some_error: '',
          },
        },
      },
      t`There was an error saving a credit card.`
    );
  }
  try {
    const addCreditCardMethod = inject('Rover.urls.addCreditCardMethod');
    const { creditCard, address, ...other } = body;
    const flattenedData = {
      ...creditCard,
      ...address,
      ...other,
    };
    const formattedData = formatBilling(flattenedData);
    // the API will throw error if nonce and postal_code both exist in the same payload
    delete formattedData.postal_code;
    const response: {
      data: { is_success: boolean, url: string, error_status: string },
    } = await axios.post(addCreditCardMethod, formattedData);
    const { is_success: isSuccess, error_status: errorMessage, url } = response.data;
    if (!isSuccess) {
      throw Error(errorMessage);
    }
    return url;
  } catch (error) {
    if (error.response) {
      throw DefaultAPIErrorHandler(error, t`There was an error saving a credit card.`);
    } else {
      throw error;
    }
  }
};



// WEBPACK FOOTER //
// ../react-lib/src/apis/creditCards.api.js